import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InputPhoneEmail from "../../components/Auth/InputPhoneEmail/InputPhoneEmail";
import { NavAuth } from "../../components/Auth/NavAuth/NavAuth";
import { useConfig } from "../../context/ConfigContext";
import withNavigateHook from "../../hooks/withNavigateHook";
import { languageData } from "../../i18n/index";
import {
  clearStoreItem,
  initUserMode,
  sendVerificationCode,
  updateUserAuth
} from "../../store/actions/actions";
import { validation } from "../../utils/Validation";
import { navigateBasedOnRestaurantCount } from "../../utils/navigationUtils";

import SocialAuthButtons from "../../components/Auth/SocialAuthButtons/SocialAuthButtons";
import "./AuthStyles.scss";
import { useLocation } from "react-router-dom";

interface PhoneEmailInterface {
  filedInput: string;
  inputType: "email" | "tel";
  countryPrefix: string;
}

type PhoneEmailDispatcherInterface = (field: string, newValue: string) => void;

function AuthentificationPage(props: any) {
  const { config } = useConfig();
  const location = useLocation();
  const { isSignupFlow } = location.state || {};
  const { establishementCount: restaurantCount = 0, isGlobalWebsite = false } = config || {};

  const dispatch = useDispatch();
  const {
    user,
    authGoogleSuccess,
    authAppleSuccess,
    sendVerificationCodeResponse,
    authRetries,
    userMode,
    authError,
  } = useSelector((state: any) => state.auth);

  const [phoneEmailState, setPhoneEmailState] = useState<PhoneEmailInterface>({
    filedInput: "",
    inputType: "email",
    countryPrefix: "",
  });

  const [loggedWithThirdParty, setLoggedWithThirdParty] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasErrorInput, setHasErrorInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasErrorServer, setHasErrorServer] = useState("");
  const [retries, setRetries] = useState(0);

  const forceEmail = props.location?.state?.forUser === "email" && user.length !== 0;
  const forcePhone = props.location?.state?.forUser === "phone" && user.length !== 0;

  const redirectedToAuth = localStorage.getItem("redirectedToAuth") === "true";
  const accessToken = localStorage.getItem("accessToken");

  const validateInput = () => {
    const phoneNumber = "+" + phoneEmailState.countryPrefix + phoneEmailState.filedInput;
    return {
      isMailValid: validation.ValidateEmail(phoneEmailState.filedInput),
      isPhone: phoneNumber && validation.ValidatePhonenumber(phoneEmailState.filedInput),
    };
  };

  useEffect(() => {

    if (!isSignupFlow) {
      dispatch(initUserMode());
    }
  }, [isSignupFlow])


  useEffect(() => {
    if (loggedWithThirdParty && redirectedToAuth) {
      props.navigate("/paiement");
      return;
    }
    if (accessToken) {
      navigateBasedOnRestaurantCount(restaurantCount, isGlobalWebsite, props.navigate);
    }
  }, [accessToken]);

  const sendVerifCode = () => {
    const { isPhone } = validateInput();
    const purePhone = "+" + phoneEmailState.countryPrefix + phoneEmailState.filedInput.replace(" ", "");

    dispatch(
      sendVerificationCode(
        {
          input: isPhone ? purePhone : phoneEmailState.filedInput,
          hashCode: "0000",
          preferredMethod: isPhone ? 1 : 0,
          mode: userMode,
        },
        false
      )
    );
  };

  const handleSubmit = () => {
    setHasError(false);
    setHasErrorInput(false);
    setHasErrorServer("");
    const { isMailValid, isPhone } = validateInput();
    const purePhone = "+" + phoneEmailState.countryPrefix + phoneEmailState.filedInput.replace(" ", "");

    if (loggedWithThirdParty) {
      if (!isMailValid && !isPhone) {
        setHasError(true);
        return;
      }
      dispatch(sendVerificationCode({ input: purePhone, hashCode: "0000", preferredMethod: 1, mode: 2 }, false));
      dispatch(updateUserAuth(isMailValid ? { email: phoneEmailState.filedInput, type: "email" } : { phone: purePhone, type: "phone" }));
      return;
    }

    if (!phoneEmailState.filedInput.trim()) {
      setHasError(true);
      setHasErrorInput(true);
      setErrorMessage("Le champ ne peut pas être vide");
      return;
    }

    if (!isMailValid && !isPhone) {
      if (/[a-zA-Z]/.test(phoneEmailState.filedInput) || forceEmail) {
        setHasError(true);
        setErrorMessage("E-mail non valide");
        return;
      } else if (!isPhone || forcePhone) {
        setHasError(true);
        setErrorMessage("Numéro non valide");
        return;
      } else {
        setHasError(true);
        setErrorMessage("E-mail ou numéro de téléphone est non valide");
        return;
      }
    }

    if (!isMailValid && forceEmail) {
      setHasError(true);
      setHasErrorInput(true);
      setErrorMessage("Invalid email");
      return;
    }

    if (!isPhone && forcePhone) {
      setHasError(true);
      setHasErrorInput(true);
      setErrorMessage("Invalid phone");
      return;
    }

    const userInfo = isMailValid ? { email: phoneEmailState.filedInput, type: "email" } : { phone: purePhone, type: "phone" };
    sendVerifCode();
    dispatch(updateUserAuth(userInfo));
    setRetries(retries + 1);

    if (phoneEmailState.filedInput.length > 245) {
      setHasErrorInput(true);
      setHasErrorServer(languageData.fr.auth["login.error.input.email.length"]);
    }
  };

  useEffect(() => {
    if (sendVerificationCodeResponse && phoneEmailState.filedInput) {
      const phoneNumber = "+" + phoneEmailState.countryPrefix + phoneEmailState.filedInput;
      const object = {
        input: validation.ValidateEmail(phoneEmailState.filedInput) ? phoneEmailState.filedInput : phoneNumber,
        hashCode: "0000",
        preferredMethod: validation.ValidateEmail(phoneEmailState.filedInput) ? 0 : 1,
        mode: userMode,
      };
      ["sendVerificationCodeResponse", "resendVerificationCodeResponse", "authError"].forEach((item) =>
        dispatch(clearStoreItem(item))
      );

      userMode === 1
        ? props.navigate("passwordPage", { replace: true, state: object })
        : props.navigate("otp", { replace: true, state: object });
    }
  });

  useEffect(() => {
    if (authError && !sendVerificationCodeResponse) {
      const serverErrors: Record<string, string> = {
        "email incorrect": languageData.fr.serverError["email.exist"],
        "email existant": languageData.fr.serverError["email.exist"],
        "phonennumber existe": languageData.fr.serverError["phone.exist"],
      };

      if (serverErrors[authError]) {
        setHasErrorInput(true);
        setHasErrorServer(serverErrors[authError]);
      }
    }
  }, [authError, authRetries]);

  useEffect(() => {
    if (authGoogleSuccess) {
      dispatch(
        updateUserAuth({
          email: authGoogleSuccess?.payloadWeb?.email,
          type: "email",
          firstName: authGoogleSuccess?.payloadWeb?.given_name,
          lastName: authGoogleSuccess?.payloadWeb?.family_name,
        })
      );
    }
  }, [authGoogleSuccess]);

  useEffect(() => {
    if (authAppleSuccess) {
      dispatch(updateUserAuth({ email: authAppleSuccess?.payload?.email, type: "email" }));
    }
  }, [authAppleSuccess]);


  const setPhoneEmailFieldValue: PhoneEmailDispatcherInterface = (field, newValue) => {
    setPhoneEmailState((prev) => ({ ...prev, [field]: newValue }));
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") handleSubmit();
  };

  return (
    <div className="authPage">
      <NavAuth />
      <div className="content">
        {/* form header */}
        <div className="form-header">
          <p className="tite-form">Inscription ou connexion</p>
          {forceEmail && <p className="sub-title"><small>Veuillez saisir votre e-mail</small></p>}
          {(forcePhone || authGoogleSuccess || loggedWithThirdParty) && <p className="sub-title">Veuillez saisir votre numéro de téléphone</p>}
          {!forceEmail && !forcePhone && !authGoogleSuccess && !loggedWithThirdParty && (
            <p className="sub-title">Veuillez saisir votre numéro de téléphone<br />ou votre e-mail</p>
          )}
        </div>

        {/* form body */}
        <div className="form-body">
          <InputPhoneEmail
            loggedWithThirdParty={loggedWithThirdParty}
            hasErrorServer={hasErrorServer}
            forceToPhone={forcePhone}
            forceToEmail={forceEmail}
            forceToGoogle={authGoogleSuccess !== null}
            state={[phoneEmailState, setPhoneEmailFieldValue]}
            setError={setHasError}
            hasError={hasError}
            handleKeyDown={handleKeyDown}
          />

          {authError !== "phonennumber existe" && (hasError || hasErrorInput) &&
            <div className="error-message">
              {errorMessage || authError}
            </div>
          }

          {authError === "phonennumber existe" && (
            <div className="error-message">
              Ce numéro de téléphone est déjà associé à une autre adresse e-mail
            </div>
          )}

          <button onClick={handleSubmit} className="btn-form continue">Continuer</button>

          {!authGoogleSuccess &&
            !authAppleSuccess && (
              <SocialAuthButtons onLoginSuccess={() => setLoggedWithThirdParty(true)} />
            )}

          <div className="content-description">
            <p>
              En continuant, vous acceptez de recevoir au numéro fourni des <b>appels</b> ou <b>SMS</b> de la part de <b>Frenchinnov</b> et/ou des sociétés partenaires/affiliées.
            </p>
            <p>
              En continuant, vous <b>acceptez nos conditions générales</b> et vous confirmez avoir lu et compris notre politique de confidentialité. Pour plus de détails, <b onClick={() => props.navigate("/apropos")}>cliquez ici.</b>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withNavigateHook(AuthentificationPage);
